import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";

import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import { BillV2 } from "../../utils/billingServiceV2";
import { createAdjustmentV2 } from "../../utils/adjustmentServiceV2";
import StatusModal from "../../components/modals/StatusModal";

interface BillAdjustmentModalProps {
  open: boolean;
  bill: BillV2 | null;
  onClose: () => void;
  onSuccess: () => void;
}

const BillAdjustmentModal: React.FC<BillAdjustmentModalProps> = ({
  open,
  bill,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    adjustmentType: "Credit",
    amount: 0,
    reason: "",
  });

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
    title: string;
  }>({
    open: false,
    type: "success",
    message: "",
    title: "",
  });

  useEffect(() => {
    if (bill && open) {
      setFormData({
        adjustmentType: "Credit",
        amount: 0,
        reason: "",
      });
    }
  }, [bill, open]);

  const handleSubmit = async () => {
    if (!bill) return;

    if (formData.amount <= 0) {
      alert("Amount must be greater than 0");
      return;
    }

    if (!formData.reason.trim()) {
      alert("Please provide a reason for adjustment");
      return;
    }

    setLoading(true);
    try {
      const res = await createAdjustmentV2({
        customerId: bill.customerId,
        contractId: bill.contractId,
        billId: bill.id,
        adjustmentType: formData.adjustmentType,
        amount: formData.amount,
        reason: formData.reason,
        requestedBy: "00000000-0000-0000-0000-000000000000", // Using placeholder as seen in other modules
      });

      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: "Adjustment Requested",
          message: "Bill adjustment request has been created successfully",
        });
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        throw res;
      }
    } catch (err: any) {
      console.error("Error creating adjustment", err);
      setStatusModal({
        open: true,
        type: "error",
        title: "Request Failed",
        message: err.message || "Failed to create adjustment request",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Make Bill Adjustment</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: "8px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                Bill Number
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {bill?.billNumber}
              </Typography>
              <Stack direction="row" spacing={4} mt={1}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="body2">{bill?.customerName}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Net Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{bill?.netAmount != null ? (bill.netAmount * -1).toLocaleString() : ""}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <CustomOutlinedSelect
              label="Adjustment Type"
              options={[
                { label: "Credit", value: "Credit" },
                { label: "Debit", value: "Debit" },
              ]}
              value={formData.adjustmentType}
              onChange={(opt) =>
                setFormData({
                  ...formData,
                  adjustmentType: opt.value as string,
                })
              }
            />

            <TextField
              label="Adjustment Amount"
              type="number"
              fullWidth
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: Number(e.target.value) })
              }
              variant="outlined"
              size="small"
              required
            />

            <TextField
              label="Reason"
              fullWidth
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) =>
                setFormData({ ...formData, reason: e.target.value })
              }
              variant="outlined"
              size="small"
              required
              placeholder="Explain why this adjustment is being made..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: "8px", px: 4 }}
          >
            {loading ? "Submitting..." : "Submit Adjustment"}
          </Button>
        </DialogActions>
      </Dialog>

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </>
  );
};

export default BillAdjustmentModal;
